import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import { useLogoutMutation } from '@/entities/session'
import { baseApi, normalizeError, setAccessToken } from '@/shared/api'

/**
 * Выход (P-AUTH-02#R-10). Сеанс кончается только по ответу бэка: `204` — закрыт, `401` — его
 * уже не было. Тогда токен и кеш сбрасываются и открывается вход без адреса возврата: вышедший
 * начинает заново. Сеть или `5xx` — cookie на бэке не снята, пользователь остаётся, где был,
 * а ошибку показывает вызывающий: тост в меню аккаунта, плашку на выборе компании.
 *
 * Сброс и переход — один такт. Сначала сброс, затем переход с `flushSync`: роутер применяет
 * новый адрес синхронным рендером, и в тот же рендер попадает сброшенный кеш. Меню аккаунта
 * зовёт `navigate`, пока смонтировано, кабинет не перерисовывается заставкой, а `GuestOnly`
 * на входе не видит сеанса из старого кеша и не возвращает в кабинет.
 *
 * `logout()` отвечает, закончился ли сеанс; `failed` — последняя попытка не удалась.
 */
export function useLogout() {
  const [logoutRequest, { isLoading, isError, error }] = useLogoutMutation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const logout = useCallback(async (): Promise<boolean> => {
    try {
      await logoutRequest().unwrap()
    } catch (reason) {
      if (normalizeError(reason).status !== 401) return false
    }

    setAccessToken(null)
    dispatch(baseApi.util.resetApiState())
    void navigate('/login', { replace: true, flushSync: true })
    return true
  }, [dispatch, logoutRequest, navigate])

  const failed = isError && normalizeError(error).status !== 401

  return { logout, isLoggingOut: isLoading, failed }
}
